/**
 * Match addressing, with no storage.
 *
 * A match is fully determined by `(agentA, agentB, seed)`, so that tuple *is* the
 * match's identity. This module only encodes and decodes it:
 *
 *   `/replay/collector~hunter~421337`
 *
 * That is the whole design. Since nothing is persisted, the id is not a database
 * key — it is a *recipe*. `/replay/:id` rebuilds the match by re-running the
 * deterministic engine from the id alone, so:
 *
 *   - there is no history API, no database, no cleanup, nothing to migrate;
 *   - a replay link is genuinely self-contained and can be shared, bookmarked or
 *     pasted anywhere, and works for a recipient who has never visited before;
 *   - "is this claim true?" is answerable by replaying it, which is what makes
 *     the platform's replayability claim meaningful rather than decorative.
 *
 * The trade-off, stated plainly: an unplayed link cannot show a scoreboard until
 * it has been played, and there is no cross-visit leaderboard. That is the right
 * trade for a client-only project, and it is the *more* honest one for a
 * verifiability story anyway — the result is reproducible from public data.
 */

export function makeMatchId(agentIds: readonly [string, string], seed: number): string {
  return `${agentIds[0]}~${agentIds[1]}~${seed}`;
}

export function parseMatchId(
  id: string,
): { agentIds: [string, string]; seed: number } | null {
  // Cricket ids are slugs (`newCricketId` strips everything outside [a-z0-9-])
  // and built-in ids are plain words, so `~` is a separator that cannot occur
  // inside a segment. That makes a plain split both correct and obvious.
  const parts = id.split("~");
  if (parts.length !== 3) return null;
  const [a, b, seedRaw] = parts as [string, string, string];
  if (a === "" || b === "") return null;
  const seed = Number(seedRaw);
  if (!Number.isFinite(seed)) return null;
  return { agentIds: [a, b], seed: Math.abs(Math.trunc(seed)) };
}
